/**
 * Klaviyo Event Integration
 * 
 * Handles 15-minute consolidation timer for quiz completion events
 * Consolidates multiple pets into a single event before sending to Klaviyo
 */

class KlaviyoEventManager {
  constructor(backendApiUrl, flowConfig = null) {
    this.backendApiUrl = backendApiUrl;
    this.flowConfig = flowConfig;
    this.timerDelay = 900000; // 15 minutes in milliseconds
    this.activeTimer = null;
    this.pendingEvent = null;
    this.lastOrigin = null;
    this.lastPath = null;
  }

  /**
   * Schedule Klaviyo event after customer/pet creation
   * @param {Object} petData - Pet data object
   * @param {Object} customerData - Customer data (ownerName, ownerEmail)
   * @param {string} petMetaobjectId - Shopify Pet Metaobject ID
   * @param {boolean} isNewPet - Whether this is a new pet (true) or existing pet being updated (false)
   */
  async scheduleKlaviyoEvent(petData, customerData, petMetaobjectId, isNewPet = true) {
    // Check if there's an existing pending event
    const existingEvent = this.loadPendingEvent(customerData.ownerEmail);

    if (existingEvent && existingEvent.pets) {
      // Add new pet to existing pending event (reset timer)
      await this.cancelScheduledEvent(existingEvent.scheduleKey);
      this.cancelPendingEvent();
      existingEvent.pets.push({
        petData: petData,
        petMetaobjectId: petMetaobjectId,
        timestamp: Date.now()
      });
      
      // Update emailConsent if provided (use most recent)
      if (customerData.emailConsent !== undefined) {
        existingEvent.profile.emailConsent = customerData.emailConsent;
      }
      this.pendingEvent = existingEvent;
      this.savePendingEvent(customerData.ownerEmail, this.pendingEvent);

      // Reset timer to full 15 minutes
      await this.startTimer(customerData.ownerEmail);
    } else {
      // No pending event exists - this is the first pet (or first pet after page reload)
      // Always create a pending "Quiz submitted" event for the first pet
      
      this.cancelPendingEvent();
      this.pendingEvent = {
        eventType: 'Quiz submitted',
        profile: {
          email: customerData.ownerEmail,
          first_name: customerData.ownerName || '',
          emailConsent: customerData.emailConsent || false
        },
        pets: [{
          petData: petData,
          petMetaobjectId: petMetaobjectId,
          timestamp: Date.now()
        }],
        cartItems: [],
        createdAt: Date.now()
      };
      this.savePendingEvent(customerData.ownerEmail, this.pendingEvent);
      await this.startTimer(customerData.ownerEmail);
    }
  }

  /**
   * Update pending event data when pet data changes
   * @param {Object} petData - Updated pet data
   * @param {string} customerEmail - Customer email
   */
  updatePendingEvent(petData, customerEmail) {
    const pendingEvent = this.loadPendingEvent(customerEmail);
    if (!pendingEvent || !pendingEvent.pets || pendingEvent.pets.length === 0) {
      
      return;
    }

    // Update the most recent pet's data
    const mostRecentPet = pendingEvent.pets[pendingEvent.pets.length - 1];
    
    // CRITICAL: Only update fields that are actually being changed, preserve pet name and metaobject ID
    const updatedPetData = { ...mostRecentPet.petData };
    // Don't overwrite name or metaobject ID - these should never change
    Object.keys(petData).forEach(key => {
      if (key !== 'name' && key !== 'ownerEmail' && key !== 'ownerName') {
        updatedPetData[key] = petData[key];
      }
    });
    mostRecentPet.petData = updatedPetData;
    mostRecentPet.timestamp = Date.now();

    // Save updated event
    this.savePendingEvent(customerEmail, pendingEvent);
    this.pendingEvent = pendingEvent;
    
  }

  /**
   * Add cart items to pending event
   * @param {Array} cartItems - Array of cart items with variant info
   * @param {string} customerEmail - Customer email
   */
  addCartItemsToEvent(cartItems, customerEmail) {
    const pendingEvent = this.loadPendingEvent(customerEmail);
    if (!pendingEvent) {
      return;
    }

    // Extract cart items per pet from cart item properties
    const itemsByPet = this.groupCartItemsByPet(cartItems, pendingEvent.pets);

    // Update pending event with cart items
    pendingEvent.cartItems = itemsByPet;
    pendingEvent.cartItemsAddedAt = Date.now();

    // Save updated event
    this.savePendingEvent(customerEmail, pendingEvent);
    this.pendingEvent = pendingEvent;
  }

  /**
   * Group cart items by pet name (from cart item properties)
   * @param {Array} cartItems - Cart items from Shopify
   * @param {Array} pets - Array of pet objects from pending event
   * @returns {Array} Array of cart item strings per pet
   */
  groupCartItemsByPet(cartItems, pets) {
    const itemsByPet = pets.map(() => []);

    // Extract flavor and quantity from cart items
    // Pet name: `_pet_name` (canonical), legacy 🐶 / pet_name
    cartItems.forEach(item => {
      const petName =
        typeof window !== 'undefined' && typeof window.getPetNameFromLineItemProperties === 'function'
          ? window.getPetNameFromLineItemProperties(item.properties)
          : item.properties?.['🐶'] || item.properties?.pet_name;
      if (!petName) return;

      // Find which pet this item belongs to
      const petIndex = pets.findIndex(p => 
        p.petData.name && p.petData.name.toLowerCase() === petName.toLowerCase()
      );

      if (petIndex >= 0) {
        // Extract flavor from variant title or properties
        const flavor = item.variant_title || item.title || 'unknown';
        const quantity = item.quantity || 1;
        itemsByPet[petIndex].push(`${flavor}: ${quantity}`);
      }
    });

    // Convert to array of comma-separated strings
    return itemsByPet.map(items => items.length > 0 ? items.join(', ') : '');
  }

  /**
   * Set last origin/path for resume URL (called by router onRouteChange)
   */
  setLastOriginPath(origin, path) {
    this.lastOrigin = origin;
    this.lastPath = path;
  }

  /**
   * Start the consolidation timer - schedules via backend KV instead of setTimeout
   * @param {string} customerEmail - Customer email
   */
  async startTimer(customerEmail) {
    this.cancelPendingEvent();

    const pendingEvent = this.loadPendingEvent(customerEmail);
    if (!pendingEvent) return;

    let remainingTime = this.timerDelay;
    if (pendingEvent.createdAt) {
      const elapsed = Date.now() - pendingEvent.createdAt;
      remainingTime = Math.max(0, this.timerDelay - elapsed);
    }

    if (remainingTime <= 0) {
      this.sendKlaviyoEvent(customerEmail);
      return;
    }

    try {
      const properties = this.formatEventProperties(pendingEvent.pets, pendingEvent.cartItems);
      const delayMinutes = Math.ceil(remainingTime / 60000);
      const res = await fetch(`${this.backendApiUrl}/api/wizard-1/schedule-quiz-event`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          eventType: pendingEvent.eventType,
          profile: pendingEvent.profile,
          properties,
          delayMinutes
        })
      });
      const data = await res.json().catch(() => ({}));
      if (data.ok && data.key) {
        pendingEvent.scheduleKey = data.key;
        this.savePendingEvent(customerEmail, pendingEvent);
      } else {
        this.fallbackToLocalTimer(customerEmail, remainingTime);
      }
    } catch (e) {
      console.warn('Schedule quiz event failed, using local timer:', e.message);
      this.fallbackToLocalTimer(customerEmail, remainingTime);
    }
  }

  /**
   * Fallback to local setTimeout when backend schedule fails
   */
  fallbackToLocalTimer(customerEmail, remainingTime) {
    if (this.activeTimer) clearTimeout(this.activeTimer);
    this.activeTimer = setTimeout(() => {
      this.sendKlaviyoEvent(customerEmail);
      this.activeTimer = null;
    }, remainingTime);
  }

  /**
   * Send "Pet updated" event (for changes after initial event was sent)
   * @param {Object} petData - Updated pet data
   * @param {Object} customerData - Customer data
   * @param {string} petMetaobjectId - Pet Metaobject ID
   */
  async sendPetUpdatedEvent(petData, customerData, petMetaobjectId) {
    try {
      // Format event properties for single pet
      const properties = this.formatEventProperties(
        [{
          petData: petData,
          petMetaobjectId: petMetaobjectId,
          timestamp: Date.now()
        }],
        [] // No cart items for update events
      );

      // Send to backend
      const response = await fetch(`${this.backendApiUrl}/api/introbox/klaviyo-event`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          eventType: 'Pet updated',
          profile: {
            email: customerData.ownerEmail,
            first_name: customerData.ownerName || ''
          },
          properties: properties
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      console.log('Klaviyo "Pet updated" event sent successfully');
    } catch (error) {
      console.error('Error sending Klaviyo "Pet updated" event:', error);
    }
  }

  /**
   * Send "Pet added" event (for pets added after initial event was sent)
   * @param {Object} petData - New pet data
   * @param {Object} customerData - Customer data
   * @param {string} petMetaobjectId - Pet Metaobject ID
   */
  async sendPetAddedEvent(petData, customerData, petMetaobjectId) {
    try {
      // Format event properties for single pet
      const properties = this.formatEventProperties(
        [{
          petData: petData,
          petMetaobjectId: petMetaobjectId,
          timestamp: Date.now()
        }],
        [] // No cart items for "Pet added" events
      );

      // Send to backend
      const response = await fetch(`${this.backendApiUrl}/api/introbox/klaviyo-event`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          eventType: 'Pet added',
          profile: {
            email: customerData.ownerEmail,
            first_name: customerData.ownerName || ''
          },
          properties: properties
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      console.log('Klaviyo "Pet added" event sent successfully');
    } catch (error) {
      console.error('Error sending Klaviyo "Pet added" event:', error);
    }
  }

  /**
   * Check if there's a pending event for this email
   * @param {string} customerEmail - Customer email
   * @returns {boolean} True if pending event exists
   */
  hasPendingEvent(customerEmail) {
    return this.loadPendingEvent(customerEmail) !== null;
  }

  /**
   * Cancel a scheduled quiz event in KV (by key)
   */
  async cancelScheduledEvent(key) {
    if (!key || !this.backendApiUrl) return;
    try {
      await fetch(`${this.backendApiUrl}/api/wizard-1/cancel-quiz-event`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key })
      });
    } catch (e) {
      console.warn('Cancel quiz event failed:', e.message);
    }
  }

  /**
   * Send event to Klaviyo via backend (send-quiz-event-now)
   * @param {string} customerEmail - Customer email
   */
  async sendKlaviyoEvent(customerEmail) {
    const pendingEvent = this.loadPendingEvent(customerEmail);
    if (!pendingEvent) {
      console.warn('No pending Klaviyo event found for:', customerEmail);
      return;
    }

    try {
      const properties = this.formatEventProperties(pendingEvent.pets, pendingEvent.cartItems);

      await this.cancelScheduledEvent(pendingEvent.scheduleKey);

      const response = await fetch(`${this.backendApiUrl}/api/wizard-1/send-quiz-event-now`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          eventType: pendingEvent.eventType,
          profile: pendingEvent.profile,
          properties
        })
      });

      

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        
        const errorMessage = errorData.message || `HTTP ${response.status}`;
        const fullError = errorData.errorDetails ? `${errorMessage}: ${JSON.stringify(errorData.errorDetails)}` : errorMessage;
        throw new Error(fullError);
      }

      // Clear pending event on success
      this.clearPendingEvent(customerEmail);
      this.pendingEvent = null;

      
      console.log('Klaviyo event sent successfully');
    } catch (error) {
      
      console.error('Error sending Klaviyo event:', error);
      // Don't clear pending event on error - allow retry
    }
  }

  /**
   * Format pet names list with proper connector
   * @param {Array} petNames - Array of pet names
   * @returns {string} Formatted string (e.g., "Max", "Max & Bella", "Max, Bella & Charlie")
   */
  formatPetNamesList(petNames) {
    if (!petNames || !Array.isArray(petNames)) {
      return '';
    }

    // Filter out null, undefined, and empty strings, then trim
    const validNames = petNames
      .filter(name => name != null && name !== '')
      .map(name => String(name).trim())
      .filter(name => name.length > 0);

    if (validNames.length === 0) {
      return '';
    }

    if (validNames.length === 1) {
      return validNames[0];
    }

    if (validNames.length === 2) {
      return `${validNames[0]} & ${validNames[1]}`;
    }

    // 3+ names: comma-separated with & before last
    const allButLast = validNames.slice(0, -1).join(', ');
    const last = validNames[validNames.length - 1];
    return `${allButLast} & ${last}`;
  }

  /**
   * Format event properties from pets data
   * @param {Array} pets - Array of pet objects
   * @param {Array} cartItems - Array of cart item strings per pet
   * @returns {Object} Formatted event properties
   */
  formatEventProperties(pets, cartItems = []) {
    
    if (!pets || pets.length === 0) {
      return {};
    }

    const properties = {
      pet_name: [],
      gender: [],
      spayed: [],
      age_group: [],
      breed: [],
      weight: [],
      composition: [],
      activity: [],
      allergies: [],
      snacks: [],
      pet_metaobject_ids: []
    };

    pets.forEach((petObj, index) => {
      const pet = petObj.petData;
      

      // Single-value fields (comma-separated)
      if (pet.name) properties.pet_name.push(pet.name);
      if (pet.gender) properties.gender.push(pet.gender);
      if (pet.spayed !== null && pet.spayed !== undefined) {
        properties.spayed.push(pet.spayed ? 'true' : 'false');
      }
      if (pet.ageGroup) properties.age_group.push(pet.ageGroup);
      if (pet.breed) properties.breed.push(pet.breed);
      if (pet.composition) properties.composition.push(pet.composition);
      // Activity: If working dog checkbox is checked, override to 'veryActive'
      let activityValue = pet.activity;
      if (pet.isWorkingDog) {
        activityValue = 'veryActive';
      }
      if (activityValue) properties.activity.push(activityValue);
      if (pet.snacks) properties.snacks.push(pet.snacks);

      // Weight (extract from JSON string and add unit)
      if (pet.weight !== null && pet.weight !== undefined) {
        try {
          // If weight is a JSON string, parse it
          let weightValue = pet.weight;
          if (typeof pet.weight === 'string' && pet.weight.startsWith('{')) {
            const weightObj = JSON.parse(pet.weight);
            weightValue = weightObj.value;
          }
          const unit = 'kg';
          properties.weight.push(`${weightValue}${unit}`);
        } catch (e) {
          // If parsing fails, try to use as-is
          properties.weight.push(pet.weight.toString());
        }
      }

      // Allergies (array of comma-separated strings)
      if (pet.allergies && Array.isArray(pet.allergies) && pet.allergies.length > 0) {
        properties.allergies.push(pet.allergies.join(', '));
      } else {
        properties.allergies.push('none');
      }

      // Pet Metaobject ID
      if (petObj.petMetaobjectId) {
        properties.pet_metaobject_ids.push(petObj.petMetaobjectId);
      }
    });

    
    // Convert arrays to final format
    const formatted = {
      pet_name: this.formatPetNamesList(properties.pet_name),
      // Deterministic, machine-mergeable per-pet names (comma-only, no '&' formatting)
      pet_names_raw: properties.pet_name.join(', '),
      gender: properties.gender.join(', '),
      spayed: properties.spayed.join(', '),
      age_group: properties.age_group.join(', '),
      breed: properties.breed.join(', ') || '',
      weight: properties.weight.join(', '),
      composition: properties.composition.join(', '),
      activity: properties.activity.join(', '),
      allergies: properties.allergies,
      snacks: properties.snacks.join(', '),
      pet_metaobject_ids: properties.pet_metaobject_ids.join(', ')
    };
    

    // Add cart items if available
    if (cartItems && cartItems.length > 0) {
      formatted.cart_items = cartItems;
    }

    // Resume link (property key stays wizard_1_resume_url for Klaviyo compatibility).
    // Path: last quiz pathname from router (locale-aware), else flow-config urlPrefix (e.g. /wizard-2).
    const ids = (pets || []).map(p => p.petMetaobjectId).filter(Boolean);
    if (ids.length > 0) {
      const origin = this.lastOrigin || (typeof window !== 'undefined' ? window.location.origin : '');
      const path = this.lastPath || (this.flowConfig?.urlPrefix || '/wizard-1');
      const base = path.startsWith('http') ? path : origin.replace(/\/$/, '') + (path.startsWith('/') ? path : '/' + path);
      const query = ids.map(id => `pet=${encodeURIComponent(id)}`).join('&');
      formatted.wizard_1_resume_url = `${base}?${query}`;
    }

    formatted.acquisition_flow = this.flowConfig?.acquisitionFlowId || 'wizard-1';
    formatted.quiz_completed_at = new Date().toISOString();

    return formatted;
  }

  /**
   * Cancel pending event and timer
   */
  cancelPendingEvent() {
    if (this.activeTimer) {
      clearTimeout(this.activeTimer);
      this.activeTimer = null;
    }
    const pe = this.pendingEvent;
    this.pendingEvent = null;
    if (pe?.scheduleKey) {
      this.cancelScheduledEvent(pe.scheduleKey);
    }
  }

  /**
   * Resume pending event on page load
   * @param {string} customerEmail - Customer email
   */
  resumePendingEvent(customerEmail) {
    const pendingEvent = this.loadPendingEvent(customerEmail);
    if (!pendingEvent) {
      return false;
    }

    // Check if timer has expired
    const elapsed = Date.now() - pendingEvent.createdAt;
    if (elapsed >= this.timerDelay) {
      // Timer expired, send immediately
      this.sendKlaviyoEvent(customerEmail);
      return true;
    }

    // Resume timer with remaining time
    this.pendingEvent = pendingEvent;
    this.startTimer(customerEmail);
    return true;
  }

  /**
   * Get localStorage key for customer email
   * @param {string} customerEmail - Customer email
   * @returns {string} Storage key
   */
  getStorageKey(customerEmail) {
    return `klaviyo_pending_event_${customerEmail}`;
  }

  /**
   * Load pending event from localStorage
   * @param {string} customerEmail - Customer email
   * @returns {Object|null} Pending event or null
   */
  loadPendingEvent(customerEmail) {
    try {
      const key = this.getStorageKey(customerEmail);
      const data = localStorage.getItem(key);
      const parsed = data ? JSON.parse(data) : null;
      
      return parsed;
    } catch (error) {
      console.error('Error loading pending Klaviyo event:', error);
      return null;
    }
  }

  /**
   * Save pending event to localStorage
   * @param {string} customerEmail - Customer email
   * @param {Object} eventData - Event data to save
   */
  savePendingEvent(customerEmail, eventData) {
    try {
      const key = this.getStorageKey(customerEmail);
      localStorage.setItem(key, JSON.stringify(eventData));
    } catch (error) {
      console.error('Error saving pending Klaviyo event:', error);
    }
  }

  /**
   * Clear pending event from localStorage
   * @param {string} customerEmail - Customer email
   */
  clearPendingEvent(customerEmail) {
    try {
      const key = this.getStorageKey(customerEmail);
      localStorage.removeItem(key);
    } catch (error) {
      console.error('Error clearing pending Klaviyo event:', error);
    }
  }
}

// Export for global access
window.KlaviyoEventManager = KlaviyoEventManager;
